import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { adminButton } from '@/components/admin/ui';
import { useToast } from '@/hooks/use-toast';
import { logEnquirySchema, useLogEnquiry, type LogEnquiryValues } from '@/hooks/useEnquiries';
import { FollowUpPicker, LeadSourceSelect } from './FollowUpControls';

const EMPTY: LogEnquiryValues = {
  client_name: '',
  client_email: '',
  client_phone: '',
  additional_info: '',
  lead_source: 'phone',
  follow_up_on: '',
};

/** Log a lead that didn't come through the website (a call, a referral, Facebook…). */
export function LogEnquiryDialog({
  open,
  onOpenChange,
  orgId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string | null | undefined;
}) {
  const { toast } = useToast();
  const logEnquiry = useLogEnquiry();
  const form = useForm<LogEnquiryValues>({ resolver: zodResolver(logEnquirySchema), defaultValues: EMPTY });

  useEffect(() => {
    if (open) form.reset(EMPTY);
  }, [open, form]);

  const onSubmit = async (values: LogEnquiryValues) => {
    if (!orgId) return;
    try {
      await logEnquiry.mutateAsync({ orgId, values });
      toast({ title: 'Enquiry logged', description: `${values.client_name} is in your inbox as a new lead.` });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Could not log the enquiry',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[17px]">Log an enquiry</DialogTitle>
          <DialogDescription>For leads that came in by phone, word of mouth or social media.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="adm-form space-y-4" noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="client_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input type="tel" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="client_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="additional_info"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What they're after</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="e.g. Fortnightly clean, 3-bed semi, has a dog" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lead_source"
              render={({ field }) => (
                <FormItem>
                  <LeadSourceSelect id="log-enquiry-source" value={field.value} onChange={field.onChange} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="follow_up_on"
              render={({ field }) => (
                <FormItem>
                  <FollowUpPicker idPrefix="log-enquiry-follow-up" value={field.value || null} onChange={(d) => field.onChange(d ?? '')} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" size="sm" className={adminButton.outline} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className={adminButton.primary} disabled={logEnquiry.isPending}>
                {logEnquiry.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Log enquiry
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
